import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { useAdminAuth, restoreAdminAuth, clearAdminAuth } from '@siclop/ksadmin-vue';
import * as authApi from '@/api/auth';
import type { LoginPayload, RegisterPayload, User } from '@/types/domain';
import { useSessionStore } from './session.store';

/**
 * Store d'authentification — FAÇADE sur l'auth du package @siclop/ksadmin-vue
 * (`useAdminAuth` + `restoreAdminAuth`, comme grimoire). On NE réinvente plus la
 * roue : le package gère login/refresh silencieux/persistance F5/RBAC. Cette
 * façade conserve l'API historique (`auth.user`, `isAuthenticated`, `isAdmin`,
 * `login/register/logout/loadMe/clearSession`) pour que les ~38 consommateurs
 * restent inchangés ; seul le MOTEUR change.
 *
 * La session est ré-hydratée au boot par `restoreAdminAuth()` (cf main.ts) puis
 * tenue à jour par le bridge du provider JWT ; un F5 garde donc la session.
 *
 * Note : toute mutation appelle `session.clear()` pour ne pas trainer un
 * activePlayerUniverseId de la session précédente (sidebar/redirection lobby).
 */
export const useAuthStore = defineStore('auth', () => {
  const admin = useAdminAuth();
  const loading = ref(false);

  // User du package -> User métier theho2. Le package expose `username`
  // (identifiant) que notre domaine nomme `login`. Les consommateurs lisent
  // surtout id / login / email.
  const user = computed<User | null>(() => {
    const u = admin.user.value as unknown as {
      id: number; username?: string; login?: string; email?: string;
      emailValidationStatus?: string; emailValidated?: boolean;
      locale?: string; avatarPath?: string | null;
    } | null;
    if (!u) return null;
    return {
      id: u.id,
      login: u.username ?? u.login ?? '',
      email: u.email ?? '',
      emailValidated: u.emailValidationStatus === 'Validated' || u.emailValidated === true,
      locale: u.locale ?? 'fr-FR',
      avatarPath: u.avatarPath ?? null,
    };
  });

  // JWT courant (chaîne) : exposé par sécurité (consommateurs éventuels) et
  // utilisé pour dériver les rôles. Repli localStorage si le ref n'est pas
  // encore peuplé (juste après le restore au boot).
  const token = computed<string | null>(
    () => admin.token.value?.token ?? localStorage.getItem('jwtToken'),
  );

  const isAuthenticated = computed(() => admin.isAuthenticated.value);

  // Accès admin : le JWT porte les rôles ; on considère admin un porteur de
  // « Administrateur » ou « WebMaster » (mêmes rôles que [Authorize(Roles=…)]
  // côté API). Sert à masquer/gater l'UI d'admin — le backend reste l'autorité.
  const ADMIN_ROLES = ['Administrateur', 'WebMaster'];
  const isAdmin = computed(() =>
    authApi.getRolesFromToken(token.value).some((r) => ADMIN_ROLES.includes(r)));

  async function login(payload: LoginPayload): Promise<void> {
    loading.value = true;
    try {
      const ok = await admin.login(payload.login, payload.password);
      // Échec = identifiants invalides (le endpoint répond 401 sans corps). Message
      // EXPLICITE (sinon extractApiError d'une Error simple -> « Erreur inconnue »).
      if (!ok) throw new Error('Pseudo/email ou mot de passe incorrect.');
      // Repart d'une session vide : le joueur choisira un univers au lobby.
      useSessionStore().clear();
    } finally {
      loading.value = false;
    }
  }

  /**
   * Crée le compte (POST /api/auth/register). NE connecte PAS : le compte est
   * en attente de validation d'email (anti-bot). Renvoie le message du back ;
   * la vue affiche alors l'écran « vérifie tes emails ». Le login ne marchera
   * qu'une fois l'email validé (refusé par le package sinon).
   */
  async function register(payload: RegisterPayload): Promise<string> {
    loading.value = true;
    try {
      const { message } = await authApi.register(payload);
      return message;
    } finally {
      loading.value = false;
    }
  }

  async function loadMe(): Promise<void> {
    // Plus de GET /users/{id} : l'état est restauré au boot (restoreAdminAuth)
    // et maintenu par le bridge. On ré-hydrate seulement par sécurité si l'appel
    // précède le restore initial.
    if (!admin.user.value) restoreAdminAuth();
  }

  async function logout(): Promise<void> {
    try {
      await admin.logout();
    } finally {
      clearAdminAuth();
      useSessionStore().clear();
    }
  }

  /** Reset local (event 'theho:unauthorized' / 401) : purge complète package. */
  function clearSession(): void {
    clearAdminAuth();
    useSessionStore().clear();
  }

  return {
    token,
    user,
    loading,
    isAuthenticated,
    isAdmin,
    login,
    register,
    loadMe,
    logout,
    clearSession,
  };
});
